import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

/**
 * Button component for saving the current search.
 * Opens a modal to name the search, then saves it via the API.
 *
 * @param {Object} props
 * @param {string} props.searchType - Type of search (e.g., 'parliamentarians', 'hansard')
 * @param {string} props.queryText - The main search query (q parameter)
 * @param {Object} props.parameters - Additional search parameters (filters, sort, etc.)
 * @param {number} props.resultCount - Number of results (for display purposes)
 * @param {function} props.onSaved - Callback when search is successfully saved
 */
function SaveSearchButton({ searchType, queryText, parameters = {}, resultCount, onSaved }) {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleOpen = () => {
    // Generate a default name based on the search
    const defaultName = queryText && queryText !== '*'
      ? `${searchType}: "${queryText.substring(0, 30)}${queryText.length > 30 ? '...' : ''}"`
      : `${searchType} search`;
    setName(defaultName);
    setOpen(true);
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    setOpen(false);
    setName('');
    setError('');
    setSuccess(false);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Please enter a name for this search');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await api.post('/saved-searches', {
        name: name.trim(),
        search_type: searchType,
        query_text: queryText || '*',
        parameters: parameters,
        result_count: resultCount,
      });

      setSuccess(true);
      if (onSaved) {
        onSaved(response.data);
      }

      // Close after a brief delay to show success
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error saving search:', err);
      if (err.response?.status === 401) {
        setError('Please log in to save searches');
      } else {
        setError(err.response?.data?.detail || 'Failed to save search');
      }
    } finally {
      setSaving(false);
    }
  };

  // Don't show button if not authenticated
  if (!isAuthenticated) {
    return (
      <Tooltip title="Log in to save searches">
        <span>
          <Button
            variant="outlined"
            size="small"
            startIcon={<BookmarkAddIcon />}
            disabled
          >
            Save Search
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<BookmarkAddIcon />}
        onClick={handleOpen}
      >
        Save Search
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Save Search</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Search saved successfully!
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Search Name"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving || success}
            helperText="Give your saved search a memorable name"
            inputProps={{ maxLength: 100 }}
          />
          {queryText && queryText !== '*' && (
            <TextField
              margin="dense"
              label="Search Query"
              fullWidth
              variant="outlined"
              value={queryText}
              disabled
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || success || !name.trim()}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SaveSearchButton;
